import {Helmet} from '@dr.pogodin/react-helmet';
import {
  DataStatus,
  Form,
  FormSubmitHandler,
  showNotification,
  WithoutBlocker,
} from 'platform/components';

import {useRef} from 'react';
import {FieldErrors} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetBranchListQuery, useGetSeriesListQuery, useGetTenantQuery} from '@dms/api/core';
import {
  useGetAuthorizationProfileQuery,
  useGetAuthorizationProfilesQuery,
  useLazyGetAuthorizationProfileQuery,
} from '@dms/api/security';
import {
  useGetDirectSaleTypesQuery,
  useGetDirectSaleVariantDefaultVarehousesPriorityQuery,
  useGetDirectSaleVariantQuery,
  useGetMaterialPriceTypesQuery,
  useGetWorkRateTypesQuery,
  usePatchDirectSaleVariantMutation,
  usePostDirectSaleVariantMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {
  buildDepositPatchBody,
  getOptionsFromSeriesList,
  SettingsTemplate,
  resolveVariantTemplateId,
} from '@dms/settings/shared';
import {
  handleApiError,
  queryParams,
  useDocumentTemplates,
  useDuplicateErrorHandler,
  usePermissions,
} from '@dms/shared';

import {useNavigate, useQueryState, ApiError, Nullish} from 'shared/web';

import {WarehouseDirectSaleVariantsDetailTabs} from './components/WarehouseDirectSaleVariantsDetailTabs/WarehouseDirectSaleVariantsDetailTabs';
import {
  WarehouseDirectSaleVariantsDetailFormSchema,
  WarehouseDirectSaleVariantsDetailFormSchemaType,
} from './types/WarehouseDirectSaleVariantsDetailFormSchema';
import {buildAccountingPatchBody} from './utils/buildAccountingPatchBody';
import {buildDocumentsPatchBody} from './utils/buildDocumentsPatchBody';
import {buildGeneralPatchBody} from './utils/buildGeneralPatchBody';
import {buildWarehouseDirectSaleVariantsDetailDefaultValues} from './utils/buildWarehouseDirectSaleVariantsDetailDefaultValues';
import {buildWarehouseDirectSaleVariantsDetailHeader} from './utils/buildWarehouseDirectSaleVariantsDetailHeader';
import {buildWarehousePriorityPatchBody} from './utils/buildWarehousePriorityPatchBody';

export function WarehouseDirectSaleVariantsDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const withoutBlockerRef = useRef<WithoutBlocker>((callback) => callback());
  const {duplicateErrorHandler} = useDuplicateErrorHandler();
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const [getTemplates, {isLoading: isTemplatesLoading}] = useDocumentTemplates();
  const standardInvoice = getTemplates('workshop-invoice');
  const internalInvoice = getTemplates('workshop-invoice_internal');

  const resolveStandardInvoiceTemplateId = (templateId: string | Nullish) =>
    resolveVariantTemplateId(
      standardInvoice.definition?.templates,
      standardInvoice.defaultTemplates.find((template) => template.isPrimary)?.id,
      templateId
    );

  const resolveInternalInvoiceTemplateId = (templateId: string | Nullish) =>
    resolveVariantTemplateId(
      internalInvoice.definition?.templates,
      internalInvoice.defaultTemplates.find((template) => template.isPrimary)?.id,
      templateId
    );

  const [warehouseDictionaryEdit, warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryEdit', 'warehouseDictionaryCreate'],
  });

  const {
    data: directSaleVariant,
    isLoading: isDirectSaleVariantLoading,
    isError: hasDirectSaleVariantError,
  } = useGetDirectSaleVariantQuery({directSaleVariantId: id as string}, {skip: isNilOrEmpty(id)});

  const {data: tenant, isLoading: isTenantLoading, isError: hasTenantError} = useGetTenantQuery();

  const {
    data: authorizationProfile,
    isLoading: isAuthorizationProfileLoading,
    isError: hasAuthorizationProfileError,
  } = useGetAuthorizationProfileQuery(
    {
      'x-tenant': tenant?.id as string,
      authorizationProfileId: directSaleVariant?.general?.authorizationProfileId as string,
    },
    {skip: isNil(tenant) || isNilOrEmpty(directSaleVariant?.general?.authorizationProfileId)}
  );

  const [lazyGetAuthorizationProfile, {isFetching: isLazyGetAuthorizationProfileFetching}] =
    useLazyGetAuthorizationProfileQuery();

  const {
    data: branchList,
    isLoading: isBranchListLoading,
    isError: hasBranchListError,
  } = useGetBranchListQuery();

  const {
    data: authorizationProfiles,
    isLoading: isAuthorizationProfilesLoading,
    isError: hasAuthorizationProfilesError,
  } = useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id as string}, {skip: isNil(tenant)});

  const {
    data: directSaleTypes,
    isLoading: isDirectSaleTypesLoading,
    isError: hasDirectSaleTypesError,
  } = useGetDirectSaleTypesQuery();

  const {
    data: workRateTypes,
    isLoading: isWorkRateTypesLoading,
    isError: hasWorkRateTypesError,
  } = useGetWorkRateTypesQuery();

  const {
    data: materialPriceTypes,
    isLoading: isMaterialPriceTypesLoading,
    isError: hasMaterialPriceTypesError,
  } = useGetMaterialPriceTypesQuery();

  const {
    data: seriesList,
    isLoading: isSeriesListLoading,
    isError: hasSeriesListError,
  } = useGetSeriesListQuery({
    type: [
      'warehouse/direct-sale',
      'warehouse/offer',
      'accounting/invoice',
      'warehouse/fast-track',
    ],
  });

  const {
    data: directSaleVariantDefaultVarehousesPriority,
    isLoading: isDirectSaleVariantDefaultVarehousesPriorityLoading,
    isError: hasDirectSaleVariantDefaultVarehousesPriorityError,
  } = useGetDirectSaleVariantDefaultVarehousesPriorityQuery();

  const [createDirectSaleVariant] = usePostDirectSaleVariantMutation();
  const [updateDirectSaleVariant] = usePatchDirectSaleVariantMutation();

  const isLoading =
    isDirectSaleVariantLoading ||
    isTenantLoading ||
    isAuthorizationProfilesLoading ||
    isAuthorizationProfileLoading ||
    isBranchListLoading ||
    isDirectSaleTypesLoading ||
    isWorkRateTypesLoading ||
    isMaterialPriceTypesLoading ||
    isSeriesListLoading ||
    isTemplatesLoading ||
    isDirectSaleVariantDefaultVarehousesPriorityLoading;

  const isError =
    hasDirectSaleVariantError ||
    hasTenantError ||
    hasAuthorizationProfilesError ||
    hasAuthorizationProfileError ||
    hasBranchListError ||
    hasDirectSaleTypesError ||
    hasWorkRateTypesError ||
    hasMaterialPriceTypesError ||
    hasSeriesListError ||
    hasDirectSaleVariantDefaultVarehousesPriorityError;

  // Every dropdown option list is resolved once here and passed down, so the tabs and the payment
  // cards share one computation instead of repeating it per rendered card.
  const standardInvoiceTemplateOptions = standardInvoice.options;
  const internalInvoiceTemplateOptions = internalInvoice.options;
  const directSaleDocSeriesOptions = getOptionsFromSeriesList(seriesList, 'warehouse/direct-sale');
  const offerDocSeriesOptions = getOptionsFromSeriesList(seriesList, 'warehouse/offer');
  const fastTrackDocSeriesOptions = getOptionsFromSeriesList(seriesList, 'warehouse/fast-track');
  const invoiceDocSeriesOptions = getOptionsFromSeriesList(seriesList, 'accounting/invoice');

  const handleSubmit: FormSubmitHandler<WarehouseDirectSaleVariantsDetailFormSchemaType> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const payloadBody = {
      general: buildGeneralPatchBody(formValues.general),
      documents: buildDocumentsPatchBody(formValues.documents),
      accounting: buildAccountingPatchBody(formValues.accounting),
      reporting: formValues.reporting,
      deposit: buildDepositPatchBody(formValues.deposit),
      warehousePriority: buildWarehousePriorityPatchBody(formValues.warehousesPriority),
    };

    if (isNotNil(id)) {
      return await updateDirectSaleVariant({
        body: payloadBody,
        directSaleVariantId: id,
      })
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('entity.warehouse.notifications.directSaleVariantUpdated')
          );
        })
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          handleNavigateBack();
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await createDirectSaleVariant(payloadBody)
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('entity.warehouse.notifications.directSaleVariantCreated'));
      })
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        handleNavigateBack();
      })
      .catch(duplicateErrorHandler);
  };

  const handleNavigateBack = () => {
    navigate(settingsRoutes.warehousesDirectSaleVariants);
  };

  const handleNavigateToInvalidTab = (
    errors: FieldErrors<WarehouseDirectSaleVariantsDetailFormSchemaType>
  ) => {
    const isGeneralTabValid = isNil(errors.general);

    const isDocumentsTabValid =
      isNil(errors.documents?.fastTrackIssue) && isNil(errors.documents?.orderOffer);

    const isPaymentTabValid =
      isNil(errors.documents?.defaultPaymentType) &&
      isNil(errors.documents?.cardPayment) &&
      isNil(errors.documents?.cashPayment) &&
      isNil(errors.documents?.cashOnDeliveryPayment) &&
      isNil(errors.documents?.bankPayment) &&
      isNil(errors.documents?.internalPayment);

    const isDepositTabValid = isNil(errors.deposit);

    const isAccountingTabValid = isNil(errors.accounting);

    if (!isGeneralTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId('general'));
    }

    if (!isDocumentsTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId('documents'));
    }

    if (!isPaymentTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId('payment'));
    }

    if (!isDepositTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId('deposit'));
    }

    if (!isAccountingTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId('accounting'));
    }
  };

  // Check edit permission only if id is not nil that means that the user is creating a new measurement unit
  const isEditDisabled = isNil(id) ? !warehouseDictionaryCreate : !warehouseDictionaryEdit;

  const header = buildWarehouseDirectSaleVariantsDetailHeader(
    id,
    directSaleVariant?.general.name,
    isEditDisabled
  );

  const defaultValues = buildWarehouseDirectSaleVariantsDetailDefaultValues({
    directSaleVariantId: id,
    directSaleVariant,
    authorizationProfileBranchId: authorizationProfile?.branchId,
    defaultWarehousesPriority: directSaleVariantDefaultVarehousesPriority,
    resolvers: {resolveStandardInvoiceTemplateId, resolveInternalInvoiceTemplateId},
  });

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.directSaleVariantDetail')} />
      <DataStatus isLoading={isLoading} isError={isError} minHeight={60}>
        <Form<WarehouseDirectSaleVariantsDetailFormSchemaType>
          mode="onChange"
          isFullHeight
          shouldWatchForUnsavedChanges
          isDisabled={isEditDisabled}
          experimentalZodSchema={WarehouseDirectSaleVariantsDetailFormSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onInvalidSubmit={(errors) => handleNavigateToInvalidTab(errors)}
        >
          {(control, formApi, _navigateWithoutBlocker, withoutBlocker) => {
            withoutBlockerRef.current = withoutBlocker;

            const handleAuthorizationProfileChange = (
              authorizationProfileId: string | string[] | number | null
            ) => {
              if (isNil(tenant) || isNilOrEmpty(authorizationProfileId)) {
                return;
              }

              lazyGetAuthorizationProfile({
                'x-tenant': tenant?.id,
                authorizationProfileId: authorizationProfileId as string,
              })
                .unwrap()
                .then((authorizationProfile) =>
                  formApi.setValue('general.branchId', authorizationProfile.branchId, {
                    shouldValidate: true,
                  })
                )
                .catch(handleApiError);
            };

            return (
              <SettingsTemplate
                header={header}
                data-testid={testIds.settings.warehousesDirectSaleVariantsDetail('template')}
              >
                <WarehouseDirectSaleVariantsDetailTabs
                  control={control}
                  formApi={formApi}
                  activeTabId={activeTabId}
                  onTabChange={(tabId) => withoutBlocker(() => setActiveTabId(tabId))}
                  onDiscard={handleNavigateBack}
                  onAuthorizationProfileChange={handleAuthorizationProfileChange}
                  isFetchingAuthorizationProfile={isLazyGetAuthorizationProfileFetching}
                  isTemplatesLoading={isTemplatesLoading}
                  directSaleVariantId={id}
                  authorizationProfiles={authorizationProfiles}
                  branchList={branchList}
                  directSaleTypes={directSaleTypes}
                  workRateTypes={workRateTypes}
                  materialPriceTypes={materialPriceTypes}
                  standardInvoiceTemplateOptions={standardInvoiceTemplateOptions}
                  internalInvoiceTemplateOptions={internalInvoiceTemplateOptions}
                  directSaleDocSeriesOptions={directSaleDocSeriesOptions}
                  offerDocSeriesOptions={offerDocSeriesOptions}
                  fastTrackDocSeriesOptions={fastTrackDocSeriesOptions}
                  invoiceDocSeriesOptions={invoiceDocSeriesOptions}
                />
              </SettingsTemplate>
            );
          }}
        </Form>
      </DataStatus>
    </>
  );
}
